import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { FiDownload, FiPrinter, FiX } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { ToggleSwitch } from "../../../../Components/Sidebar/ToggleSwitch";

interface Props {
  sections: Array<{
    number: string | number;
    displayNumber?: string | number;
    title: string;
  }>;
  excluded: string[];
  setExcluded: Dispatch<SetStateAction<string[]>>;
  clientPreview: boolean;
  onClientPreviewChange: (enabled: boolean) => void;
  onClose: () => void;
  onExport: (excludedSections: string[]) => Promise<void>;
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1400;
  background: rgba(20, 50, 59, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
`;
const Dialog = styled.div`
  width: min(540px, 94vw);
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  background: white;
  border: 1px solid #e6ece9;
  border-radius: 18px;
  box-shadow: 0 24px 60px rgba(20, 50, 59, 0.22);
  overflow: hidden;
  color: #14323b;
  button {
    font: inherit;
    cursor: pointer;
  }
  button:disabled {
    cursor: default;
    opacity: 0.45;
  }
  button:focus-visible {
    outline: 2px solid #35a46e;
    outline-offset: 3px;
  }
`;
const Header = styled.div`
  padding: 24px 26px 18px;
  border-bottom: 1px solid #eef1ef;
  h2 {
    margin: 8px 0 5px;
    font-family: Figtree, sans-serif;
    font-size: 19px;
    font-weight: 800;
  }
  p {
    margin: 0;
    font-size: 12.5px;
    color: #8a97a0;
    line-height: 1.5;
  }
`;
const Line = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;
const Eyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #2c8c5e;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;
const Close = styled.button`
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border: 1px solid #eef1ef;
  border-radius: 8px;
  background: white;
  color: #9aa7ac;
  display: grid;
  place-items: center;
`;
const Selection = styled(Line)`
  margin-top: 16px;
  font-size: 12px;
  color: #5a6c72;
  span:last-child {
    display: flex;
    gap: 8px;
  }
`;
const Pill = styled.button`
  padding: 5px 12px;
  border: 1px solid #e4eae7;
  border-radius: 100px;
  background: white;
  color: #5a6c72;
  font-size: 11px !important;
  font-weight: 600 !important;
`;
const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 10px 16px;
`;
const Row = styled.button`
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 11px 12px;
  border-radius: 11px;
  border: 0;
  width: 100%;
  background: white;
  text-align: left;
  &:hover {
    background: #fafbfa;
  }
`;
const Number = styled.span`
  width: 18px;
  flex-shrink: 0;
  font-size: 10.5px;
  font-weight: 700;
  color: #c3cdc8;
`;
const Name = styled.span`
  flex: 1;
  min-width: 0;
  color: #22343b;
  font-size: 13px;
  font-weight: 600;
  small {
    display: block;
    font-size: 11px;
    font-weight: 400;
    margin-top: 2px;
    color: #8a97a0;
  }
`;
const Footer = styled(Line)`
  padding: 14px 26px 20px;
  border-top: 1px solid #eef1ef;
  p {
    color: #a2afb4;
    font-size: 11px;
    line-height: 1.45;
    margin: 0;
  }
  @media (max-width: 480px) {
    flex-wrap: wrap;
  }
`;
const Preview = styled(Row)`
  width: auto;
  margin: 14px 26px 0;
  flex-shrink: 0;
  background: #fafbfa;
  border: 1px solid #ecefed;
`;
const Export = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-shrink: 0;
  padding: 11px 20px;
  background: #14323b;
  color: white;
  border: 0;
  border-radius: 10px;
  font-size: 13px !important;
  font-weight: 600 !important;
`;
const ErrorText = styled.p`
  color: #b42318;
  padding: 0 26px;
  font-size: 12px;
`;

export const ExportPdfModal = ({
  sections,
  excluded,
  setExcluded,
  clientPreview,
  onClientPreviewChange,
  onClose,
  onExport,
}: Props) => {
  const { t } = useTranslation();
  const label = (key: string, options = {}) =>
    t(`dueDiligence.finalReport.exportModal.${key}`, options);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const dialog = useRef<HTMLDivElement>(null);
  const exporting = useRef(false);
  const count = sections.filter(
    (s) => !excluded.includes(String(s.number)),
  ).length;

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.current?.focus();
    return () => {
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, []);

  const close = () => {
    if (!exporting.current) onClose();
  };
  const generate = async () => {
    if (exporting.current || !count) return;
    exporting.current = true;
    setBusy(true);
    setError(false);
    try {
      await onExport(excluded);
      onClose();
    } catch {
      setError(true);
    } finally {
      exporting.current = false;
      setBusy(false);
    }
  };

  return createPortal(
    <Overlay
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          close();
        }
        if (e.key === "Tab") {
          const buttons = dialog.current?.querySelectorAll<HTMLButtonElement>(
            "button:not(:disabled)",
          );
          if (!buttons?.length) {
            e.preventDefault();
            return;
          }
          const first = buttons[0],
            last = buttons[buttons.length - 1];
          if (
            e.shiftKey &&
            (document.activeElement === first ||
              document.activeElement === dialog.current)
          ) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }}
    >
      <Dialog
        ref={dialog}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pdf-export-title"
        aria-describedby="pdf-export-description"
        aria-busy={busy}
      >
        <Header>
          <Line>
            <Eyebrow>
              <FiDownload size={14} />
              {label("eyebrow")}
            </Eyebrow>
            <Close aria-label={label("close")} onClick={close} disabled={busy}>
              <FiX />
            </Close>
          </Line>
          <h2 id="pdf-export-title">{label("title")}</h2>
          <p id="pdf-export-description">{label("description")}</p>
          <Selection>
            <span aria-live="polite">
              {label("count", { count, total: sections.length })}
            </span>
            <span>
              <Pill disabled={busy} onClick={() => setExcluded([])}>
                {label("all")}
              </Pill>
              <Pill
                disabled={busy}
                onClick={() =>
                  setExcluded(sections.map((s) => String(s.number)))
                }
              >
                {label("none")}
              </Pill>
            </span>
          </Selection>
        </Header>
        <Body>
          {sections.map((section) => {
            const number = String(section.number);
            const included = !excluded.includes(number);
            return (
              <Row
                key={number}
                role="switch"
                aria-checked={included}
                aria-label={section.title}
                disabled={busy}
                onClick={() =>
                  setExcluded((prev) =>
                    included
                      ? [...prev, number]
                      : prev.filter((n) => n !== number),
                  )
                }
              >
                <Number>
                  {String(section.displayNumber ?? number).padStart(2, "0")}
                </Number>
                <Name>
                  {section.title}
                  <small>{label(included ? "included" : "excluded")}</small>
                </Name>
                <ToggleSwitch aria-hidden="true" $isOn={included} />
              </Row>
            );
          })}
        </Body>
        {error && <ErrorText role="alert">{label("error")}</ErrorText>}
        <Preview
          role="switch"
          aria-checked={clientPreview}
          aria-label={label("preview")}
          disabled={busy}
          onClick={() => onClientPreviewChange(!clientPreview)}
        >
          <ToggleSwitch aria-hidden="true" $isOn={clientPreview} />
          <Name>
            {label("preview")}
            <small>{label("previewDescription")}</small>
          </Name>
        </Preview>
        <Footer>
          <p>{label("footer")}</p>
          <Export onClick={generate} disabled={busy || !count}>
            <FiPrinter size={14} />
            {label(busy ? "generating" : "export", { count })}
          </Export>
        </Footer>
      </Dialog>
    </Overlay>,
    document.body,
  );
};
